import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import UserContext from "./UserContext";
import { INITIAL_USER_STATE } from "./Context.model";
import { setAuthToken } from "common/HttpClient";
import { mapCognitoPayloadToProfile, isUserUnAuthenticatedException, performLogout } from "common/Auth/cognitoAuth";
import { startTokenRefresh, stopTokenRefresh } from "common/HttpClient/tokenManager";
import { setAuthTokenPAdmin } from "common/HttpClient/http-client-pAdmin";
import { syncCustomerHeadersFromProfile, resolveCustomerContextForHeaders } from "common/HttpClient/CustomerContextHeaders";
import { resetAppActiveCustomersCache } from "common/Services/ActiveCustomersMaster";
import { syncCustomerRoleOptions } from "common/Services/CustomerRolesMaster";
import { setMeActiveCustomer } from "common/Services/MeSession.services";
import PropTypes from "prop-types";
import EffectiveAccessProvider from "./EffectiveAccessProvider";
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  isPlatformAdminProfile,
  platformAdminUsesCustomerContextFromMe,
} from "common/Utilities/Role.utils";
import {
  buildStoredCustomerInfo,
  findCustomerAccessRow,
  hasStoredSelectedCustomer,
  parseStoredSelectedCustomer,
} from "common/Utilities/CustomerSelection.utils";
const PROFILE = "profile";
const SELECTED_CUSTOMER = "selected_customer";
const IDLE_LOGOUT_MS = 15 * 60 * 1000;
const IDLE_ACTIVITY_THROTTLE_MS = 60 * 1000;

const hydratePlatformAdminHeaderCustomer = (profile, setHeaderSelectedCustomer) => {
  if (!isPlatformAdminProfile(profile)) {
    return;
  }
  if (hasStoredSelectedCustomer()) {
    const stored = parseStoredSelectedCustomer();
    const accessRow = findCustomerAccessRow(profile.customerAccess, stored);
    setHeaderSelectedCustomer(buildStoredCustomerInfo(stored, accessRow, profile));
    return;
  }
  setHeaderSelectedCustomer(null);
};

const UserContextProvider = ({ children }) => {
  const [userProfile, setUserProfile] = useState(INITIAL_USER_STATE);
  const [customerList, setCustomerList] = useState(null);
  const [activeCustomersList, setActiveCustomersListState] = useState(null);
  const [customerRoleOptions, setCustomerRoleOptionsState] = useState([]);
  const [customerRoleLabelMap, setCustomerRoleLabelMapState] = useState({});
  const [headerSelectedCustomer, setHeaderSelectedCustomer] = useState(null);
  const logoutRef = useRef(null);
  const idleTimeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const loadUserInformation = async (profile) => {
    if (!profile) {
      return;
    }
    
    try {
      const session = await fetchAuthSession();
      if (session.tokens && session.tokens.accessToken) {
        profile.token = session.tokens.accessToken.toString();
      }
      
    } catch (error) {
      console.error("Error getting token from Amplify session:", error);
      if (!profile.token) {
        console.warn("No token available from Amplify or profile");
      }
    }
    
    try {
      localStorage.setItem(PROFILE, JSON.stringify(profile));
      setAuthToken(profile.token);
      setAuthTokenPAdmin(profile.token);
      syncCustomerHeadersFromProfile(profile, getCustomerCode);
      
      if (Array.isArray(profile?.customerRoleOptions)) {
        setCustomerRoleOptionsState(profile.customerRoleOptions);
      }
      if (
        profile?.customerRoleLabelMap &&
        typeof profile.customerRoleLabelMap === "object"
      ) {
        setCustomerRoleLabelMapState(profile.customerRoleLabelMap);
      }
      hydratePlatformAdminHeaderCustomer(profile, setHeaderSelectedCustomer);
      setUserProfile({
        isLoggedin: true,
        profile: { ...profile },
      });
      startTokenRefresh();
    } catch (error) {
      console.error("Error loading user information:", error);
      if (Array.isArray(profile?.customerRoleOptions)) {
        setCustomerRoleOptionsState(profile.customerRoleOptions);
      }
      if (
        profile?.customerRoleLabelMap &&
        typeof profile.customerRoleLabelMap === "object"
      ) {
        setCustomerRoleLabelMapState(profile.customerRoleLabelMap);
      }
      hydratePlatformAdminHeaderCustomer(profile, setHeaderSelectedCustomer);
      setUserProfile({
        isLoggedin: true,
        profile: { ...profile },
      });
    }
  };

  const logout = useCallback(async () => {
    stopTokenRefresh();
    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current);
      idleTimeoutRef.current = null;
    }
    setUserProfile(INITIAL_USER_STATE);
    setAuthToken(null);
    setAuthTokenPAdmin(null);
    setCustomerList(null);
    setActiveCustomersListState(null);
    resetAppActiveCustomersCache();
    setCustomerRoleOptionsState([]);
    setCustomerRoleLabelMapState({});
    setHeaderSelectedCustomer(null);

    await performLogout({
      PROFILE_KEY: PROFILE,
      SELECTED_CUSTOMER_KEY: SELECTED_CUSTOMER,
    });
  }, []);

  logoutRef.current = logout;

  useEffect(() => {
    const onCustomerReset = () => {
      setHeaderSelectedCustomer(null);
    };
    window.addEventListener("customerReset", onCustomerReset);
    return () => window.removeEventListener("customerReset", onCustomerReset);
  }, []);

  useEffect(() => {
    const onCustomerChanged = async () => {
      try {
        const profileSnapshot = await new Promise((resolve) => {
          setUserProfile((prev) => {
            resolve(prev?.profile ?? null);
            return prev;
          });
        });
        if (!profileSnapshot) {
          return;
        }

        const { options, labelMap } = await syncCustomerRoleOptions(
          profileSnapshot,
          setCustomerRoleOptionsState,
          setCustomerRoleLabelMapState
        );

        setUserProfile((prev) => {
          if (!prev?.profile) {
            return prev;
          }
          const updatedProfile = {
            ...prev.profile,
            customerRoleOptions: options,
            customerRoleLabelMap: labelMap,
          };
          localStorage.setItem(PROFILE, JSON.stringify(updatedProfile));
          return {
            isLoggedin: true,
            profile: updatedProfile,
          };
        });
      } catch {
        // ignore sync errors
      }
    };
    window.addEventListener("customerChanged", onCustomerChanged);
    return () => window.removeEventListener("customerChanged", onCustomerChanged);
  }, []);

  useEffect(() => {
    if (!userProfile?.isLoggedin) return;

    const scheduleIdleLogout = () => {
      if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
      idleTimeoutRef.current = setTimeout(() => {
        idleTimeoutRef.current = null;
        if (logoutRef.current) logoutRef.current();
      }, IDLE_LOGOUT_MS);
    };

    const onActivity = () => {
      const now = Date.now();
      if (now - lastActivityRef.current >= IDLE_ACTIVITY_THROTTLE_MS) {
        lastActivityRef.current = now;
        scheduleIdleLogout();
      }
    };

    lastActivityRef.current = Date.now();
    scheduleIdleLogout();

    const events = ["mousedown", "mousemove", "keydown", "scroll", "touchstart", "click"];
    events.forEach((ev) => window.addEventListener(ev, onActivity));

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, onActivity));
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
        idleTimeoutRef.current = null;
      }
    };
  }, [userProfile?.isLoggedin]);
  const getUserProfile = async () => {
    try {
      const session = await fetchAuthSession();
      
      if (!session.tokens || !session.tokens.idToken) {
        const userData = localStorage.getItem(PROFILE);
        if (userData) {
          try {
            const parsedUserData = JSON.parse(userData);
            if (parsedUserData) {
              setAuthToken(parsedUserData.token);
              setAuthTokenPAdmin(parsedUserData.token);
              syncCustomerHeadersFromProfile(parsedUserData, getCustomerCode);
              setUserProfile({
                isLoggedin: true,
                profile: parsedUserData,
              });
              return;
            }
          } catch {
          }
        }
        logout();
        return;
      }

      const idToken = session.tokens.idToken;
      const payload = idToken.payload;
      const accessToken = session.tokens.accessToken.toString();
      
      const profile = mapCognitoPayloadToProfile(payload, accessToken);

      localStorage.setItem(PROFILE, JSON.stringify(profile));
      
      setAuthToken(profile.token);
      setAuthTokenPAdmin(profile.token);
      syncCustomerHeadersFromProfile(profile, getCustomerCode);
      setUserProfile({
        isLoggedin: true,
        profile,
      });
      
      startTokenRefresh();
    } catch (error) {
      if (isUserUnAuthenticatedException(error)) {
        const userData = localStorage.getItem(PROFILE);
        if (userData) {
          try {
            const parsedUserData = JSON.parse(userData);
            if (parsedUserData) {
              setAuthToken(parsedUserData.token);
              setAuthTokenPAdmin(parsedUserData.token);
              syncCustomerHeadersFromProfile(parsedUserData, getCustomerCode);
              setUserProfile({
                isLoggedin: true,
                profile: parsedUserData,
              });
              return;
            }
          } catch {
          }
        }
      }
      console.error("Error getting user profile from Amplify:", error);
      logout();
    }
  };
  const getCustomerCode = () => {
    const selectedCustomer = localStorage.getItem(SELECTED_CUSTOMER);
    const userData = localStorage.getItem(PROFILE);
    try {
      const parsedSelected = JSON.parse(selectedCustomer || "{}");
      const parsedUserData = JSON.parse(userData || "{}");

      const fromSelected = parsedSelected?.customerCode;
      if (
        fromSelected != null &&
        String(fromSelected).trim() !== ""
      ) {
        return fromSelected;
      }

      if (isPlatformAdminProfile(parsedUserData)) {
        return undefined;
      }

      const fromMe =
        parsedUserData?.activeCustomerCode ?? parsedUserData?.customerCode;
      if (fromMe != null && String(fromMe).trim() !== "") {
        return fromMe;
      }
      return undefined;
    } catch {
      logout();
      return undefined;
    }
  };
  const getCustomerTimeZone = () => {
    const profile = userProfile?.profile;
    if (platformAdminUsesCustomerContextFromMe(profile)) {
      if (headerSelectedCustomer?.timeZone) {
        return headerSelectedCustomer.timeZone;
      }
      return profile?.timezone ?? "";
    }
    if (isPlatformAdminProfile(profile)) {
      return profile?.timezone;
    }
    const selectedCustomer = localStorage.getItem(SELECTED_CUSTOMER);
    try {
      const parsedSelected = JSON.parse(selectedCustomer || "{}");
      if (parsedSelected?.timeZone) {
        return parsedSelected.timeZone;
      }
    } catch {
      return userProfile?.profile?.timezone;
    }
    return userProfile?.profile?.timezone;
  };
  const getCustomerName = () => {
    const profile = userProfile?.profile;
    if (platformAdminUsesCustomerContextFromMe(profile)) {
      if (headerSelectedCustomer?.name) {
        return headerSelectedCustomer.name;
      }
      return "";
    }
    return userProfile?.profile?.activeCustomerName;
  };

  const getSelectedCustomerId = () => {
    return userProfile?.profile?.activeCustomerId;
  };

  const setCustomersList = useCallback((list) => {
    setCustomerList(list);
  }, []);

  const setActiveCustomersList = useCallback((list) => {
    setActiveCustomersListState(Array.isArray(list) ? list : []);
  }, []);

  const setCustomerRoleOptions = useCallback((options) => {
    setCustomerRoleOptionsState(Array.isArray(options) ? options : []);
  }, []);

  const setCustomerRoleLabelMap = useCallback((labelMap) => {
    setCustomerRoleLabelMapState(
      labelMap && typeof labelMap === "object" ? labelMap : {}
    );
  }, []);

  const getCustomerRoleOptions = () => customerRoleOptions;

  const getCustomersList = () => {
    return customerList;
  };

  const getActiveCustomersList = () => activeCustomersList;

  const getCustomerRoleLabelMap = () => customerRoleLabelMap;

  const switchActiveCustomer = useCallback(
    async (customer) => {
      const profile = userProfile?.profile;
      if (!profile) {
        return { error: "Not logged in." };
      }
      return setMeActiveCustomer(profile, customer, loadUserInformation);
    },
    [userProfile]
  );

  const contextValues = useMemo(() => {
    return {
      userProfile,
      getUserProfile,
      loadUserInformation,
      logout,
      getCustomerCode,
      getSelectedCustomerId,
      getCustomerTimeZone,
      getCustomerName,
      customerList,
      setCustomersList,
      getCustomersList,
      activeCustomersList,
      setActiveCustomersList,
      getActiveCustomersList,
      customerRoleOptions,
      setCustomerRoleOptions,
      getCustomerRoleOptions,
      customerRoleLabelMap,
      setCustomerRoleLabelMap,
      getCustomerRoleLabelMap,
      headerSelectedCustomer,
      setHeaderSelectedCustomer,
      switchActiveCustomer,
    };
  }, [
    userProfile,
    customerList,
    activeCustomersList,
    customerRoleOptions,
    customerRoleLabelMap,
    headerSelectedCustomer,
    switchActiveCustomer,
  ]);
  return (
    <UserContext.Provider value={contextValues}>
      <EffectiveAccessProvider>{children}</EffectiveAccessProvider>
    </UserContext.Provider>
  );
};

export default UserContextProvider;

UserContextProvider.propTypes = {
  children: PropTypes.any,
};
